import {AuthConfiguration, BridgeRuntime} from "@bridge/okta-auth";
import {UrlBuilder} from "@common-core/runtime-js/util";
import {Partition} from "@common-core/runtime-js/partition";
import {Runtime, RuntimeTransform} from "@common-core/runtime-js/runtime";
import {BridgeBarRuntime} from "@common-core/react-bridge-bar";
import {BoltIcon, CarSolidIcon, DocumentTextIcon} from "@interstate/components/Icons";
import {features} from "../features";
import {Environment} from "./Environment";
import {Metadata} from "./Metadata";
import {bridgeOverride} from "./bridgeProviderOverride";

const interstateVersion = "2.0.0";

const landingPageUrlDz =
  "https://mycoxautomotive.dangerzone.coxautoservices.com/";
const landingPageUrlLive = "https://mycoxautomotive.coxautoinc.com/";

const headers: Record<string, string> = {
  "accept": "application/json",
  "content-type": "application/json"
};

const enum BridgeClient {
  PRODUCTION = "0oaumndm6412vvKyP357",
  NON_PRODUCTION = "0oaumn4aagc4OaElL357"
}

export function buildRedirectUri(runtime: Runtime<Metadata>): string {
  const deployment = runtime.deployment;
  return UrlBuilder.builder(deployment.secure)
    .withHostname(deployment.hostname)
    .withPort(deployment.port)
    .withPathname("/login/callback")
    .withParameters(() => {
      return runtime.partition === Partition.NON_PROD
        ? {partition: Partition.NON_PROD}
        : null;
    })
    .withParameters(runtime.parameters)
    .build();
}

export function buildAuthConfig(
  bridgeRuntime: BridgeRuntime,
  clientId: string,
  runtime: Runtime<Metadata>
): AuthConfiguration {
  return {
    idp: bridgeOverride(bridgeRuntime),
    clientId,
    redirectUri: buildRedirectUri(runtime),
    scopes: [
      "bridge.signin",
      "openid",
      "profile",
      "enterprise.commons-operations-admin.read"
    ],
    renewTokensOnExpiration: true
  };
}

export const environments: Record<
  string,
  RuntimeTransform<Metadata, Environment>
> = {
  // Local UI with Local Backend
  "local-local/non-prod": runtime => {
    return {
      ...dzNonProd(runtime),
      backend: {baseUri: "http://localhost:8080", headers},
      springBff: "http://localhost:8080/graphql"
    };
  },
  "local-local/prod": runtime => {
    return {
      ...dzProd(runtime),
      backend: {baseUri: "http://localhost:8080", headers},
      springBff: "http://localhost:8080/graphql"
    };
  },
  // Local UI with Dangerzone Backend
  "local-dz/non-prod": runtime => {
    return dzNonProd(runtime);
  },
  "local-dz/prod": runtime => {
    return dzProd(runtime);
  },
  // Local UI with Live Backend
  "local-live/non-prod": runtime => {
    return liveNonProd(runtime);
  },
  "local-dz/us-east-1/prod": runtime => {
    return dzProd(runtime);
  },
  // Dangerzone
  "dz/non-prod": runtime => {
    return dzNonProd(runtime);
  },
  "dz-integration/non-prod": runtime => {
    return dzNonProd(runtime);
  },
  "dz/prod": runtime => {
    return dzProd(runtime);
  },
  "dz-legacy/non-prod": runtime => {
    return dzNonProd(runtime);
  },
  "dz-legacy/prod": runtime => {
    return dzProd(runtime);
  },
  // for Backend BFF Deployment tests
  "dz/it/us-east-1/prod": runtime => {
    return {
      ...dzProd(runtime),
      springBff:
        "https://us-east-1-bff.dangerzone.coat2.coxautoservices.com/graphql",
      appsync:
        "https://us-east-1.coat-appsync-bff.coat2.dangerzone.coxautoservices.com/graphql",
      backend: {
        baseUri: "https://us-east-1-bff.dangerzone.coat2.coxautoservices.com",
        headers
      }
    };
  },
  "dz/it/us-west-2/prod": runtime => {
    return {
      ...dzProd(runtime),
      springBff:
        "https://us-east-1-bff.dangerzone.coat2.coxautoservices.com/graphql",

      appsync:
        "https://us-west-2.coat-appsync-bff.coat2.dangerzone.coxautoservices.com/graphql",
      backend: {
        baseUri: "https://us-west-2-bff.dangerzone.coat2.coxautoservices.com",
        headers
      }
    };
  },
  "dz-integration/prod": runtime => {
    return dzProd(runtime);
  },
  // Live
  "live-integration/non-prod": runtime => {
    return liveNonProd(runtime);
  },
  "live/non-prod": runtime => {
    return liveNonProd(runtime);
  },
  "live-new/non-prod": runtime => {
    return liveNonProd(runtime);
  },
  "live-new-integration/non-prod": runtime => {
    return liveNonProd(runtime);
  },
  "live/prod": runtime => {
    return liveProd(runtime);
  },
  "live-integration/prod": runtime => {
    return liveProd(runtime);
  },
  "live-new/prod": runtime => {
    return liveProd(runtime);
  },
  "live-new-integration/prod": runtime => {
    return liveProd(runtime);
  }
};

/**
 *  Environment Definitions used when running at URLs:
 *   https://localhost.coxautoservices.com:8443?partition=non-prod
 *   https://coat2.dangerzone.coxautoservices.com?partition=non-prod
 *
 */
const dzNonProd = (runtime: Runtime<Metadata>): Environment => {
  return {
    interstateVersion,
    applicationId: "2e96522e-6a04-49d0-8be9-0dee2af1c494",
    auth: buildAuthConfig(
      BridgeRuntime.DZ_NON_PRODUCTION,
      // Keep DZ NON-PROD on DZ auth to preserve integration tests
      BridgeClient.NON_PRODUCTION,
      runtime
    ),
    backend: {
      baseUri: "https://bff.dangerzone.coat2.coxautoservices.com",
      headers
    },
    springBff: "https://bff.dangerzone.coat2.coxautoservices.com/graphql",
    appsync:
      "https://coat-appsync-bff.coat2.dangerzone.coxautoservices.com/graphql",
    bridgeBar: "dz_np" as BridgeBarRuntime,
    landingPage: `${landingPageUrlDz}?partition=${runtime.partition}`,
    ldClientSideID: "620519d94d6d4813df2b4004",
    sidebarComponents: [
      {
        id: "cvi-vin-search",
        path: "/search-vehicles",
        icon: CarSolidIcon,
        script: {
          id: "cvi-vin-search-module",
          source: {
            module:
              "https://cvi-admin-tools.coxautoinventory-np.com/cvi-vin-search-component.js"
          },
          async: false,
          defer: false
        },
        element: "cvi-vin-search-component",
        attributes: {deployment: "dz", partition: "non-prod"},
        preload: true
      },
      {
        id: "example-component",
        path: "/example",
        icon: BoltIcon,
        script: {
          id: "pbc-prototype-module",
          source: {
            module:
              "https://dangerzone.wappr.coxautoservices.com/web-component-application-prototype.esm.js"
          },
          async: false,
          defer: false
        },
        element: "web-component-application-prototype",
        attributes: {
          "deployment": "dz",
          "partition": "non-prod",
          "common-org-id": ""
        },
        preload: true,
        featureFlag: features.dev.sidebarComponents.example
      },
      {
        id: "product-updates",
        path: "/product-updates",
        icon: DocumentTextIcon,
        script: {
          id: "product-updates-admin-module",
          source: {
            module:
              "https://admin.product-updates.dangerzone.coxautoservices.com/main.bundle.esm.js",
            fallback:
              "https://admin.product-updates.dangerzone.coxautoservices.com/main.bundle.js"
          },
          async: false,
          defer: false
        },
        element: "product-updates-admin",
        attributes: {
          "base-path": "/product-updates"
        },
        preload: true,
        featureFlag: features.dev.productUpdates.adminComponent
      }
    ],
    tabComponents: [
      {
        id: "pbc-prototype",
        script: {
          id: "pbc-prototype-module",
          source: {
            module:
              "https://dangerzone.wappr.coxautoservices.com/web-component-application-prototype.esm.js",
            fallback:
              "https://dangerzone.wappr.coxautoservices.com/web-component-application-prototype.js"
          }
        },
        element: "web-component-application-prototype",
        contained: false
      },
      {
        id: "bad-component",
        script: {
          id: "bad-component-module",
          source: {
            classic:
              "https://dangerzone.wappr.coxautoservices.com/bad-component.js"
          }
        },
        element: "bad-component"
      }
    ],
    historyComponent: {
      id: "operational-hierarchy-history",
      script: {
        id: "operational-hierarchy-history-module",
        source: {
          module:
            "https://static.oh-history-ui.common-org.coxautoservices.com/coat-custom-element.mjs"
        },
        async: false,
        defer: false
      },
      element: "operational-hierarchy-history",
      attributes: {deployment: "live", partition: "non-prod"},
      preload: true
    },
    contextualCommentsComponent: {
      id: "contextual-comments",
      script: {
        id: "contextual-comments-module",
        source: {
          module:
            "https://static.comments.coxautoservices.com/contextual-comments.esm.js",
          fallback:
            "https://static.comments.coxautoservices.com/contextual-comments.js"
        }
      },
      element: "enterprise-product-experience-contextual-comments",
      attributes: {
        "partition": "non-prod",
        "common-org-id": "5090acce-0217-48d6-b2c1-5ace90d455c3", //NON-PROD COX AUTO DEALER COORGID
        "namespace": "commons-operations-admin-tool",
        "description": "Comments for COAT"
      }
    }
  };
};

/**
 *  Environment Definitions used when running at URLs:
 *   https://localhost.coxautoservices.com:8443
 *   https://coat2.dangerzone.coxautoservices.com
 */
const dzProd = (runtime: Runtime<Metadata>): Environment => {
  return {
    interstateVersion,
    applicationId: "2ca4aec9-452a-4b54-8b19-ed3e16432064",
    auth: buildAuthConfig(
      BridgeRuntime.DZ_PRODUCTION,
      // Intentionally using live non-prod client to match existing behavior
      BridgeClient.NON_PRODUCTION,
      runtime
    ),
    backend: {
      baseUri: "https://bff.dangerzone.coat2.coxautoservices.com",
      headers
    },
    springBff: "https://bff.dangerzone.coat2.coxautoservices.com/graphql",
    appsync:
      "https://coat-appsync-bff.coat2.dangerzone.coxautoservices.com/graphql",
    bridgeBar: "dz_prod" as BridgeBarRuntime,
    landingPage: `${landingPageUrlDz}?partition=${runtime.partition}`,
    ldClientSideID: "620519d94d6d4813df2b4004",
    sidebarComponents: [
      {
        id: "cvi-vin-search",
        path: "/search-vehicles",
        icon: CarSolidIcon,
        script: {
          id: "cvi-vin-search-module",
          source: {
            module:
              "https://cvi-admin-tools.coxautoinventory.com/cvi-vin-search-component.js"
          },
          async: false,
          defer: false
        },
        element: "cvi-vin-search-component",
        preload: true
      },
      {
        id: "example-component",
        path: "/example",
        icon: BoltIcon,
        script: {
          id: "pbc-prototype-module",
          source: {
            module:
              "https://dangerzone.wappr.coxautoservices.com/web-component-application-prototype.esm.js"
          },
          async: false,
          defer: false
        },
        element: "web-component-application-prototype",
        attributes: {
          "deployment": "live",
          "partition": "non-prod",
          "common-org-id": ""
        },
        preload: true,
        featureFlag: features.dev.sidebarComponents.example
      }
    ],
    tabComponents: [],
    historyComponent: {
      id: "operational-hierarchy-history",
      script: {
        id: "operational-hierarchy-history-module",
        source: {
          module:
            "https://static.oh-history-ui.common-org.coxautoservices.com/coat-custom-element.mjs"
        },
        async: false,
        defer: false
      },
      element: "operational-hierarchy-history",
      attributes: {deployment: "live", partition: "non-prod"},
      preload: true
    },
    contextualCommentsComponent: {
      id: "contextual-comments",
      script: {
        id: "contextual-comments-module",
        source: {
          module:
            "https://static.comments.coxautoservices.com/contextual-comments.esm.js",
          fallback:
            "https://static.comments.coxautoservices.com/contextual-comments.js"
        }
      },
      element: "enterprise-product-experience-contextual-comments",
      attributes: {
        "partition": "non-prod",
        "common-org-id": "5090acce-0217-48d6-b2c1-5ace90d455c3", //NON-PROD COX AUTO DEALER COORGID
        "namespace": "commons-operations-admin-tool",
        "description": "Comments for COAT"
      }
    }
  };
};

/**
 *  Environment Definitions used when running at URLs:
 *   https://coat2.coxautoinc.com?partition=non-prod
 *   https://coat.coxautoinc.com?partition=non-prod
 */
const liveNonProd = (runtime: Runtime<Metadata>): Environment => {
  return {
    interstateVersion,
    applicationId: "a99ca99d-e408-4337-97e7-f005ab5b7551",
    auth: buildAuthConfig(
      BridgeRuntime.NON_PRODUCTION,
      BridgeClient.NON_PRODUCTION,
      runtime
    ),
    backend: {
      baseUri: "https://bff.coat2.coxautoinc.com",
      headers
    },
    springBff: "https://bff.coat2.coxautoinc.com/graphql",
    appsync: "https://coat-appsync-bff.coat2.coxautoinc.com/graphql",
    bridgeBar: "qa" as BridgeBarRuntime,
    landingPage: `${landingPageUrlLive}?partition=${runtime.partition}`,
    ldClientSideID: "620519d94d6d4813df2b4006",
    sidebarComponents: [
      {
        id: "cvi-vin-search",
        path: "/search-vehicles",
        icon: CarSolidIcon,
        script: {
          id: "cvi-vin-search-module",
          source: {
            module:
              "https://cvi-admin-tools.coxautoinventory-np.com/cvi-vin-search-component.js"
          },
          async: false,
          defer: false
        },
        element: "cvi-vin-search-component",
        attributes: {deployment: "live", partition: "non-prod"},
        preload: true
      },
      {
        id: "example-component",
        path: "/example",
        icon: BoltIcon,
        script: {
          id: "pbc-prototype-module",
          source: {
            module:
              "https://wappr.coxautoinc.com/web-component-application-prototype.esm.js"
          },
          async: false,
          defer: false
        },
        element: "web-component-application-prototype",
        attributes: {
          "deployment": "live",
          "partition": "non-prod",
          "common-org-id": ""
        },
        preload: true,
        featureFlag: features.dev.sidebarComponents.example
      },
      {
        id: "product-updates",
        path: "/product-updates",
        icon: DocumentTextIcon,
        script: {
          id: "product-updates-admin-module",
          source: {
            module:
              "https://admin.product-updates.dangerzone.coxautoservices.com/main.bundle.esm.js",
            fallback:
              "https://admin.product-updates.dangerzone.coxautoservices.com/main.bundle.js"
          },
          async: false,
          defer: false
        },
        element: "product-updates-admin",
        attributes: {
          "base-path": "/product-updates"
        },
        preload: true,
        featureFlag: features.dev.productUpdates.adminComponent
      }
    ],
    tabComponents: [
      {
        id: "pbc-prototype",
        script: {
          id: "pbc-prototype-module",
          source: {
            module:
              "https://wappr.coxautoinc.com/web-component-application-prototype.esm.js",
            fallback:
              "https://wappr.coxautoinc.com/web-component-application-prototype.js"
          }
        },
        element: "web-component-application-prototype"
      },
      {
        id: "common-consumer-coorg-back-office-ui",
        script: {
          id: "common-consumer-coorg-back-office-ui",
          source: {
            module:
              "https://integ-back-office-ui-wfe.vin-cedricnp.cloud/back-office-ui/web-components/back-office-ui-web-comp-latest.js",
            fallback:
              "https://integ-back-office-ui-wfe.vin-cedricnp.cloud/back-office-ui/web-components/back-office-ui-web-comp-latest.js"
          }
        },
        element: "common-consumer-coorg-back-office-ui",
        attributes: {
          partition: "non-production"
        }
      },
      {
        id: "cvi-dealer-landing-page",
        script: {
          id: "cvi-dealer-landing-page",
          source: {
            module:
              "https://cvi-home.coxautoinventory-np.com/cvi-dealer-landing-page.js",
            fallback:
              "https://cvi-home.coxautoinventory-np.com/cvi-dealer-landing-page.js"
          }
        },
        element: "cvi-dealer-landing-page",
        contained: false
      },
      {
        id: "dealer-user-search-component",
        script: {
          id: "dealer-user-search-component",
          source: {
            module:
              "https://admin.bridge.coxautoinc.com/components/user-search/dealer-user-search-component.esm.js",
            fallback:
              "https://admin.bridge.coxautoinc.com/components/user-search/dealer-user-search-component.js"
          }
        },
        element: "dealer-user-search-component",
        hash: "users",
        attributes: {deployment: "live", partition: "non-prod"}
      },
      {
        id: "entitlement-manager-component",
        script: {
          id: "entitlement-manager-component",
          source: {
            module:
              "https://ui.org-app-entitlement.coxautoservices.com/entitlement-manager-component.esm.js",
            fallback:
              "https://ui.org-app-entitlement.coxautoservices.com/entitlement-manager-component.js"
          }
        },
        element: "entitlement-manager-component",
        hash: "entitlements",
        attributes: {deployment: "live", partition: "non-prod"}
      },
      {
        id: "epam-provisioning-component",
        script: {
          id: "epam-provisioning-component",
          source: {
            module: "https://ui.np.dcpnc.app.coxautoinc.com/main.bundle.esm.js",
            fallback: "https://ui.np.dcpnc.app.coxautoinc.com/main.bundle.js"
          }
        },
        element: "dcpnc-admin-tool",
        hash: "provisioning",
        attributes: {deployment: "live", partition: "non-prod"}
      }
    ],
    historyComponent: {
      id: "operational-hierarchy-history",
      script: {
        id: "operational-hierarchy-history-module",
        source: {
          module:
            "https://static.oh-history-ui.common-org.coxautoservices.com/coat-custom-element.mjs"
        },
        async: false,
        defer: false
      },
      element: "operational-hierarchy-history",
      attributes: {deployment: "live", partition: "non-prod"},
      preload: true
    },
    contextualCommentsComponent: {
      id: "contextual-comments",
      script: {
        id: "contextual-comments-module",
        source: {
          module:
            "https://static.comments.coxautoservices.com/contextual-comments.esm.js",
          fallback:
            "https://static.comments.coxautoservices.com/contextual-comments.js"
        }
      },
      element: "enterprise-product-experience-contextual-comments",
      attributes: {
        "partition": "non-prod",
        "common-org-id": "5090acce-0217-48d6-b2c1-5ace90d455c3", //NON-PROD COX AUTO DEALER COORGID
        "namespace": "commons-operations-admin-tool",
        "description": "Comments for COAT"
      }
    }
  };
};

/**
 *  Environment Definitions used by when running at URLs:
 *   https://coat2.coxautoinc.com
 *   https://coat.coxautoinc.com
 */
const liveProd = (runtime: Runtime<Metadata>): Environment => {
  return {
    interstateVersion,
    applicationId: "e093ea7a-a94a-4c35-8270-cd0a4bd84ffd",
    auth: buildAuthConfig(
      BridgeRuntime.PRODUCTION,
      BridgeClient.PRODUCTION,
      runtime
    ),
    backend: {
      baseUri: "https://bff.coat2.coxautoinc.com",
      headers
    },
    springBff: "https://bff.coat2.coxautoinc.com/graphql",
    appsync: "https://coat-appsync-bff.coat2.coxautoinc.com/graphql",
    bridgeBar: "prod" as BridgeBarRuntime,
    landingPage: `${landingPageUrlLive}?partition=${runtime.partition}`,
    ldClientSideID: "620519d94d6d4813df2b4006",
    sidebarComponents: [
      {
        id: "cvi-vin-search",
        path: "/search-vehicles",
        icon: CarSolidIcon,
        script: {
          id: "cvi-vin-search-module",
          source: {
            module:
              "https://cvi-admin-tools.coxautoinventory.com/cvi-vin-search-component.js"
          },
          async: false,
          defer: false
        },
        element: "cvi-vin-search-component",
        attributes: {deployment: "live", partition: "prod"},
        preload: true
      },
      {
        id: "example-component",
        path: "/example",
        icon: BoltIcon,
        script: {
          id: "pbc-prototype-module",
          source: {
            module:
              "https://wappr.coxautoinc.com/web-component-application-prototype.esm.js"
          },
          async: false,
          defer: false
        },
        element: "web-component-application-prototype",
        attributes: {
          "deployment": "live",
          "partition": "prod",
          "common-org-id": ""
        },
        preload: true,
        featureFlag: features.dev.sidebarComponents.example
      }
    ],
    tabComponents: [
      {
        id: "common-consumer-coorg-back-office-ui",
        script: {
          id: "common-consumer-coorg-back-office-ui",
          source: {
            module:
              "https://prod-back-office-ui-wfe.vin-cedric.cloud/back-office-ui/web-components/back-office-ui-web-comp-latest.js",
            fallback:
              "https://prod-back-office-ui-wfe.vin-cedric.cloud/back-office-ui/web-components/back-office-ui-web-comp-latest.js"
          }
        },
        element: "common-consumer-coorg-back-office-ui",
        attributes: {
          partition: "production"
        }
      },
      {
        id: "cvi-dealer-landing-page",
        script: {
          id: "cvi-dealer-landing-page",
          source: {
            module:
              "https://cvi-home.coxautoinventory.com/cvi-dealer-landing-page.js",
            fallback:
              "https://cvi-home.coxautoinventory.com/cvi-dealer-landing-page.js"
          }
        },
        element: "cvi-dealer-landing-page",
        contained: false
      },
      {
        id: "dealer-user-search-component",
        script: {
          id: "dealer-user-search-component",
          source: {
            module:
              "https://admin.bridge.coxautoinc.com/components/user-search/dealer-user-search-component.esm.js",
            fallback:
              "https://admin.bridge.coxautoinc.com/components/user-search/dealer-user-search-component.js"
          }
        },
        element: "dealer-user-search-component",
        hash: "users",
        attributes: {deployment: "live", partition: "prod"}
      },
      {
        id: "entitlement-manager-component",
        script: {
          id: "entitlement-manager-component",
          source: {
            module:
              "https://ui.org-app-entitlement.coxautoservices.com/entitlement-manager-component.esm.js",
            fallback:
              "https://ui.org-app-entitlement.coxautoservices.com/entitlement-manager-component.js"
          }
        },
        element: "entitlement-manager-component",
        hash: "entitlements",
        attributes: {deployment: "live", partition: "prod"}
      },
      {
        id: "epam-provisioning-component",
        script: {
          id: "epam-provisioning-component",
          source: {
            module: "https://ui.dcpnc.app.coxautoinc.com/main.bundle.esm.js",
            fallback: "https://ui.dcpnc.app.coxautoinc.com/main.bundle.js"
          }
        },
        element: "dcpnc-admin-tool",
        hash: "provisioning",
        attributes: {deployment: "live", partition: "prod"}
      }
    ],
    historyComponent: {
      id: "operational-hierarchy-history",
      script: {
        id: "operational-hierarchy-history-module",
        source: {
          module:
            "https://static.oh-history-ui.common-org.coxautoservices.com/coat-custom-element.mjs"
        },
        async: false,
        defer: false
      },
      element: "operational-hierarchy-history",
      attributes: {deployment: "live", partition: "prod"},
      preload: true
    },
    contextualCommentsComponent: {
      id: "contextual-comments",
      script: {
        id: "contextual-comments-module",
        source: {
          module:
            "https://static.comments.coxautoservices.com/contextual-comments.esm.js",
          fallback:
            "https://static.comments.coxautoservices.com/contextual-comments.js"
        }
      },
      element: "enterprise-product-experience-contextual-comments",
      attributes: {
        "partition": "prod",
        "common-org-id": "a8af26f8-461f-4071-becc-ed2228a1710d", //PROD COX AUTO DEALER COORGID
        "namespace": "commons-operations-admin-tool",
        "description": "Comments for COAT"
      }
    }
  };
};
